'use client';

import * as React from 'react';
import { useState, useEffect, startTransition, useMemo, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { X, ArrowRight } from '@phosphor-icons/react';
import {
  BriefcaseIcon,
  CartIcon,
  ClockIcon,
  GiftIcon,
  GridIcon,
  HomeIcon,
  InfoIcon,
  MapPinIcon,
  MoonIcon,
  PhoneIcon,
  QuestionIcon,
  SunIcon,
  TruckIcon,
  UserIcon,
} from '@/components/icons';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { useCartStore, selectCartItemsCount, selectCartTotal, selectHasHydrated } from '@/stores/cart-store';
import { useAuthStore, selectIsAuthenticatedSafe } from '@/stores/auth-store';
import { useAddressStore, selectSelectedAddress, selectAddressHasHydrated } from '@/stores/address-store';
import { useCheckoutStore } from '@/stores/checkout-store';
import { FloatingMiniCart } from '@/components/features/cart/floating-mini-cart';
import { ScrollToTop } from '@/components/features/catalog/scroll-to-top';
import { ymCartOpen, ymPhoneClick, ymSocialClick, tmrCartOpen, tmrPhoneClick, tmrSocialClick } from '@/lib/analytics';

interface HeaderProps {
  className?: string;
}

// Оптимизированная кнопка для лучшего INP
const MagneticButton = React.memo(({ 
  children, 
  className,
  onClick,
}: { 
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  return (
    <button
      className={cn(
        className, 
        'active:scale-[0.98] active:transition-none',
        'touch-none' // Отключает touch-события для лучшей производительности
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
});
MagneticButton.displayName = 'MagneticButton';

export function Header({ className }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeHover, setActiveHover] = useState<string | null>(null);
  const pathname = usePathname();
  const { resolvedTheme, setTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Оптимизированный scroll handler с throttle
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Блокируем скролл при открытом меню (меню раскрывается поверх контента)
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  // Cart store - используем селекторы для предотвращения лишних ре-рендеров
  const hasHydrated = useCartStore(selectHasHydrated);
  const cartItemsCount = useCartStore(selectCartItemsCount);
  const cartTotal = useCartStore(selectCartTotal);
  
  // Checkout store
  const openCheckout = useCheckoutStore((state) => state.openCheckout);

  // Auth store — используем safe-селектор чтобы избежать hydration mismatch.
  // На сервере и до rehydrate localStorage это всегда false → JSX совпадает с server-render.
  const isAuthenticated = useAuthStore(selectIsAuthenticatedSafe);
  const openAuthModal = useAuthStore((state) => state.openAuthModal);

  // Address store
  const addressHasHydrated = useAddressStore(selectAddressHasHydrated);
  const selectedAddress = useAddressStore(selectSelectedAddress);
  const openAddressSheet = useAddressStore((state) => state.openSheet);

  // Use resolvedTheme only after mount to prevent hydration mismatch
  const isDark = mounted ? resolvedTheme === 'dark' : false;

  // Мемоизированные вычисления
  const displayCartCount = useMemo(() => hasHydrated ? cartItemsCount : 0, [hasHydrated, cartItemsCount]);
  const displayCartTotal = useMemo(() => hasHydrated ? cartTotal : 0, [hasHydrated, cartTotal]);
  const deliveryAddress = useMemo(() => addressHasHydrated ? (selectedAddress?.address || null) : null, [addressHasHydrated, selectedAddress]);
  
  // Мемоизированное форматирование цены (toLocaleString дорогое)
  const formattedCartTotal = useMemo(() => {
    if (displayCartTotal <= 0) return 'Корзина';
    // Быстрое форматирование вместо toLocaleString
    return `${Math.round(displayCartTotal).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} ₽`;
  }, [displayCartTotal]);
  
  const phoneNumber = '8 (863) 333-50-50';
  
  // Мемоизированные обработчики
  const handleOpenCheckout = useCallback(() => {
    // Цели «открыл корзину» отправляем только если корзина реально открылась:
    // без адреса openCheckout разворачивает выбор адреса и выходит, а метрика
    // засчитывала бы открытие, которого не было.
    startTransition(() => {
      openCheckout();
      if (useCheckoutStore.getState().isOpen) {
        ymCartOpen();
        tmrCartOpen();
      }
    });
  }, [openCheckout]);
  
  const handleOpenAddressSheet = useCallback(() => {
    startTransition(() => {
      openAddressSheet();
    });
  }, [openAddressSheet]);
  
  const handleOpenAuthModal = useCallback(() => {
    startTransition(() => {
      openAuthModal();
    });
  }, [openAuthModal]);
  
  const handleToggleTheme = useCallback(() => {
    startTransition(() => {
      setTheme(isDark ? 'light' : 'dark');
    });
  }, [isDark, setTheme]);
  
  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const menuItems = [
    { icon: HomeIcon, label: 'Главная', href: '/', subtitle: 'На главную' },
    { icon: GiftIcon, label: 'Акции', href: '/actions', subtitle: 'Скидки и бонусы' },
    { icon: TruckIcon, label: 'Доставка', href: '/delivery', subtitle: 'Условия и зоны' },
    { icon: QuestionIcon, label: 'FAQ', href: '/faq', subtitle: 'Ответы на вопросы' },
    { icon: InfoIcon, label: 'О нас', href: '/about', subtitle: 'Знакомство с командой' },
    { icon: BriefcaseIcon, label: 'Вакансии', href: '/vacancies', subtitle: 'Работа у нас' },
  ];

  return (
    <>
      {/* Main Header — top сдвигается вниз на высоту smart-app-banner
          (CSS-переменная --app-banner-h устанавливается баннером при маунте,
          сбрасывается в 0px при закрытии — header плавно возвращается). */}
      <div
        className="fixed left-2 right-2 z-50 md:left-0 md:right-0 pointer-events-none transition-[top] duration-300 ease-out"
        style={{ top: 'calc(var(--app-banner-h, 0px) + 0.5rem)' }}
      >
        <header
          className={cn(
            'max-w-7xl mx-auto pointer-events-auto',
            'transition-all duration-300 ease-out',
            'rounded-card md:max-w-[calc(80rem-3rem)] relative',
            className
          )}
        >
        {/* Header Background */}
        {/* Мобилка: сплошной градиент. ПК: стекло */}
        <div className={cn(
          'absolute inset-0 transition-all duration-500 overflow-hidden rounded-card',
          !mounted 
            ? 'bg-gradient-to-r from-[var(--violet-400)] to-[var(--primary)]'
            : isDark
              ? 'md:backdrop-blur-2xl bg-[var(--bg-primary)]/90 md:bg-[var(--bg-primary)]/60 border border-white/[0.08]'
              : 'md:backdrop-blur-2xl bg-[var(--primary)]/95 md:bg-[var(--primary)]/70'
        )}>
          {/* Light theme — полупрозрачный градиент */}
          {mounted && !isDark && (
            <div 
              className="absolute inset-0 rounded-card"
              style={{
                background: `linear-gradient(to right, var(--bonus-platinum), var(--primary))`,
                opacity: 0.7,
              }}
            />
          )}
        </div>

        {/* Floating shadow.
            shadow-[var(--primary)]/10 убран как мёртвый код: проверено сборкой —
            эта запись кладёт цвет в саму тень (--tw-shadow), а не в её цвет
            (--tw-shadow-color), и стоящий рядом shadow-md объявлен в CSS ниже и
            перетирает её. Фиолетового ореола у шапки никогда не рисовалось. */}
        <div className="absolute inset-0 -z-10 rounded-card shadow-float" />

        {/* Gradient Line - only show on dark theme when scrolled */}
        {mounted && isDark && isScrolled && (
          <div className="absolute bottom-0 left-0 right-0 h-[1px] rounded-b-card bg-gradient-to-r from-transparent via-[var(--primary)]/30 to-transparent" />
        )}

        <div className="relative px-4 sm:px-6">
          <div className="flex items-center justify-between h-[60px]">
            
            {/* Left: Logo + Address */}
            <div className="flex items-center gap-4">
              {/* Logo */}
              <Link href="/" className="relative z-10">
                {/* Desktop: full logo, Mobile: compact logohead */}
                <Image
                  src="/logo_white.svg"
                  alt="Азиаточка"
                  width={160}
                  height={40}
                  className="h-9 w-auto hidden md:block"
                  priority
                />
                <Image
                  src="/logohead.svg"
                  alt="Азиаточка"
                  width={40}
                  height={40}
                  className="h-9 w-auto md:hidden"
                  priority
                />
              </Link>

              {/* Address Selector - Mobile */}
              <div className="sm:hidden">
                <MagneticButton
                  className="group flex items-center gap-1.5 h-10 pl-2 pr-2.5 rounded-pill bg-white/15 hover:bg-white/25 transition-[background-color] duration-100"
                  onClick={handleOpenAddressSheet}
                >
                  <MapPinIcon className="h-4 w-4 text-white flex-shrink-0" weight="fill" />
                  <span className="text-xs font-medium text-white max-w-[100px] truncate">
                    {deliveryAddress || 'Адрес'}
                  </span>
                </MagneticButton>
              </div>

              {/* Address Selector - Desktop */}
              <div className="hidden sm:block">
                <MagneticButton
                  className="group flex items-center gap-2.5 h-10 pl-2.5 pr-3 rounded-pill bg-white/15 hover:bg-white/25 transition-[background-color] duration-100"
                  onClick={handleOpenAddressSheet}
                >
                  <div className="flex items-center justify-center w-7 h-7 rounded-card bg-white/15">
                    <MapPinIcon className="h-4 w-4 text-white" weight="fill" />
                  </div>
                  <span className="text-sm font-medium text-white max-w-[140px] md:max-w-[180px] truncate">
                    {deliveryAddress || 'Укажите адрес'}
                  </span>
                  <ArrowRight className="h-4 w-4 text-white/60 transition-transform duration-200 group-hover:translate-x-0.5" weight="fill" />
                </MagneticButton>
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-1.5 md:gap-3">
              
              {/* Cart - Desktop only */}
              <div className="hidden md:block">
                <MagneticButton
                  className={cn(
                    'relative flex items-center gap-2.5 h-10 px-4 rounded-card font-medium text-sm will-change-transform',
                    'transition-[background-color,opacity] duration-100',
                    displayCartCount > 0
                      ? 'bg-white text-[var(--primary)] hover:bg-white/90'
                      : 'bg-white/15 text-white hover:bg-white/25'
                  )}
                  onClick={handleOpenCheckout}
                >
                  <div className="relative">
                    <CartIcon className="h-[18px] w-[18px]" weight="fill" />
                    {displayCartCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center rounded-pill bg-[var(--primary)] text-white text-[9px] font-bold">
                        {displayCartCount}
                      </span>
                    )}
                  </div>
                  <span className="hidden sm:inline">
                    {formattedCartTotal}
                  </span>
                </MagneticButton>
              </div>

              {/* Theme Toggle - Mobile only */}
              {mounted && (
                <div className="md:hidden">
                  <MagneticButton
                    className="flex items-center justify-center w-10 h-10 rounded-pill bg-white/15 text-white hover:bg-white/25 transition-[background-color] duration-100"
                    onClick={handleToggleTheme}
                  >
                    {isDark ? (
                      <SunIcon className="h-[18px] w-[18px]" weight="fill" />
                    ) : (
                      <MoonIcon className="h-[18px] w-[18px]" weight="fill" />
                    )}
                  </MagneticButton>
                </div>
              )}

              {/* Icon Buttons Group */}
              <div className="flex items-center gap-1 md:gap-1.5">
                {/* Profile - Mobile */}
                <div className="md:hidden">
                  {isAuthenticated ? (
                    <Link href="/profile">
                      <MagneticButton
                        className="relative flex items-center justify-center w-10 h-10 rounded-pill bg-white/15 text-white hover:bg-white/25 transition-[background-color] duration-100"
                      >
                        <UserIcon className="h-[18px] w-[18px]" weight="fill" />
                      </MagneticButton>
                    </Link>
                  ) : (
                    <MagneticButton
                      className="relative flex items-center justify-center w-10 h-10 rounded-pill bg-white/15 text-white hover:bg-white/25 transition-[background-color] duration-100"
                      onClick={handleOpenAuthModal}
                    >
                      <UserIcon className="h-[18px] w-[18px]" weight="fill" />
                    </MagneticButton>
                  )}
                </div>
                {/* Profile - Desktop */}
                <div className="hidden md:block">
                  {isAuthenticated ? (
                    <Link href="/profile">
                      <MagneticButton
                        className="relative flex items-center justify-center w-10 h-10 rounded-pill bg-white/15 text-white hover:bg-white/25 transition-[background-color] duration-100"
                      >
                        <UserIcon className="h-[18px] w-[18px]" weight="fill" />
                      </MagneticButton>
                    </Link>
                  ) : (
                    <MagneticButton
                      className="relative flex items-center justify-center w-10 h-10 rounded-pill bg-white/15 text-white hover:bg-white/25 transition-[background-color] duration-100"
                      onClick={handleOpenAuthModal}
                    >
                      <UserIcon className="h-[18px] w-[18px]" weight="fill" />
                    </MagneticButton>
                  )}
                </div>

                {/* Theme Toggle - Desktop */}
                {mounted && (
                  <div className="hidden md:block">
                    <MagneticButton
                      className="flex items-center justify-center w-10 h-10 rounded-pill bg-white/15 text-white hover:bg-white/25 transition-all duration-200"
                      onClick={handleToggleTheme}
                    >
                      {isDark ? (
                        <SunIcon className="h-[18px] w-[18px]" weight="fill" />
                      ) : (
                        <MoonIcon className="h-[18px] w-[18px]" weight="fill" />
                      )}
                    </MagneticButton>
                  </div>
                )}

                {/* Menu Button */}
                <button
                  className="flex items-center justify-center w-10 h-10 rounded-pill bg-white/15 text-white hover:bg-white/25 transition-[background-color] duration-100 active:scale-[0.98] active:transition-none"
                  onClick={() => setIsMenuOpen((v) => !v)}
                >
                  {isMenuOpen ? (
                    <X className="h-[18px] w-[18px]" weight="fill" />
                  ) : (
                    <GridIcon className="h-[18px] w-[18px]" weight="fill" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Menu — раскрывается из шапки на всех экранах */}
        <div
          className={cn(
            'grid overflow-hidden transition-[grid-template-rows] duration-400 ease-out',
            isMenuOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div className="overflow-hidden">
            {/* Divider */}
            <div className="mx-4 md:mx-6 h-px bg-white/10" />

            {/* Navigation Grid — адаптивная сетка */}
            <div className="px-4 pt-3 pb-3 md:px-6 md:pt-5 md:pb-2 max-h-[calc(100dvh_-_var(--app-banner-h,0px)_-_5rem)] md:max-h-[calc(100dvh_-_var(--app-banner-h,0px)_-_9rem)] overflow-y-auto overscroll-contain -webkit-overflow-scrolling-touch">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                {menuItems.map((item) => {
                  const isActive = pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={handleCloseMenu}
                      onMouseEnter={() => setActiveHover(item.href)}
                      onMouseLeave={() => setActiveHover(null)}
                      className={cn(
                        'group relative block p-3.5 md:p-5 rounded-card',
                        'transition-colors duration-200',
                        'bg-white/10 hover:bg-white/15 active:bg-white/20',
                        isActive && 'bg-white/20'
                      )}
                    >
                      <div className={cn(
                        'w-9 h-9 md:w-10 md:h-10 rounded-card flex items-center justify-center mb-2 md:mb-3',
                        'transition-colors duration-200',
                        'bg-white/15',
                        'group-hover:bg-white group-hover:scale-105',
                        isActive && 'bg-white'
                      )}>
                        <item.icon
                          className={cn(
                            'h-4 w-4 md:h-5 md:w-5 transition-colors',
                            isActive || activeHover === item.href ? 'text-[var(--primary)]' : 'text-white'
                          )}
                          weight="fill"
                        />
                      </div>
                      <h3 className="text-base md:text-lg font-heading font-bold text-white mb-0.5 leading-tight">
                        {item.label}
                      </h3>
                      <p className="text-xs md:text-sm text-white/50 leading-tight">
                        {item.subtitle}
                      </p>
                      <ArrowRight
                        className="absolute top-3.5 right-3.5 md:top-5 md:right-5 h-4 w-4 text-white/30 transition-all duration-200 group-hover:translate-x-1 group-hover:text-white"
                        weight="fill"
                      />
                    </Link>
                  );
                })}

                {/* Support tile */}
                <div className="relative block p-3.5 md:p-5 rounded-card bg-white/10">
                  <h3 className="text-base md:text-lg font-heading font-bold text-white mb-1 leading-tight">
                    Поддержка
                  </h3>
                  <a
                    href={`tel:${phoneNumber.replace(/\D/g, '')}`}
                    onClick={() => { ymPhoneClick(); tmrPhoneClick(); }}
                    className="block text-xs md:text-sm font-semibold hover:!text-white transition-colors mb-2.5"
                    style={{ color: 'rgba(255,255,255,0.7)' }}
                  >
                    <span>{phoneNumber}</span>
                  </a>
                  <div className="flex gap-1.5">
                    <a href="https://max.ru/u/f9LHodD0cOJy8oPBxUr-x-mis-JgEXUn4KSVirJM9Ix_OdGwXP4A3anpCCU" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-pill flex items-center justify-center hover:bg-white/25 transition-colors" style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                      <svg viewBox="-100 -100 960 958" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" clipRule="evenodd" d="M392.07 186.804C292.648 181.591 215.063 250.579 197.954 358.557C183.786 447.977 208.906 556.935 230.392 562.419C239.505 564.745 261.436 547.971 277.392 532.925C280.391 530.096 284.927 529.62 288.445 531.771C313.317 546.98 341.477 558.41 372.522 560.038C474.59 565.39 565.042 485.507 570.388 383.43C575.735 281.353 494.139 192.157 392.07 186.804ZM225.581 705.191C221.732 702.466 216.428 703.205 213.202 706.646C170.082 752.636 59.7277 784.896 54.6727 722.126C54.6727 672.946 43.6266 631.503 31.4646 585.873C16.5692 529.989 0 467.824 0 377.475C0 162.027 176.694 0 386.194 0C595.694 0 759.951 169.938 759.951 379.567C759.951 589.196 590.466 754.95 388.176 754.95C316.409 754.95 281.583 744.841 225.581 705.191Z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="mx-6 h-px bg-white/10" />
              <div className="px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <a
                    href={`tel:${phoneNumber.replace(/\D/g, '')}`}
                    className="flex items-center gap-2 text-white hover:text-white/80 transition-colors"
                  >
                    <PhoneIcon className="h-4 w-4" weight="fill" />
                    <span className="font-semibold text-sm">{phoneNumber}</span>
                  </a>
                  <div className="flex items-center gap-1.5">
                    <ClockIcon className="h-3.5 w-3.5 text-white/50" weight="fill" />
                    <span className="text-sm text-white/50">11:00 – 22:00</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href="https://max.ru/u/f9LHodD0cOJy8oPBxUr-x-mis-JgEXUn4KSVirJM9Ix_OdGwXP4A3anpCCU"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-9 h-9 rounded-pill bg-white/15 hover:bg-[var(--max-red)] transition-colors"
                  >
                    <span className="text-white font-bold text-xs">M</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      </div>

      {/* Backdrop — закрывает меню при клике вне шапки */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={handleCloseMenu}
        />
      )}

      {/* Floating Mini Cart - Mobile only */}
      <FloatingMiniCart />
      <ScrollToTop />
    </>
  );
}
